"use client";

import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface CardDotsProps {
  /** Light ink that reads on the dark plate. */
  tone?: "parchment" | "white";
  className?: string;
}

/**
 * A calm field of drifting dots for the card plates — the same dot idiom as the
 * header wordmark (DotLogo), but ambient rather than assembling: a handful of
 * dots float slowly and wrap at the edges. Decorative (aria-hidden).
 *
 * Performance: one canvas per card, but the animation only runs while the card
 * is on screen (IntersectionObserver) and honours prefers-reduced-motion (a
 * single static frame). Dot count scales with the plate area and is capped.
 */
export function CardDots({ tone = "parchment", className }: CardDotsProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    const css = getComputedStyle(document.documentElement);
    const colour =
      tone === "white"
        ? "#ffffff"
        : css.getPropertyValue("--color-parchment").trim() || "#f3eee3";

    interface Dot {
      x: number;
      y: number;
      vx: number;
      vy: number;
      r: number;
      a: number;
    }
    let dots: Dot[] = [];
    let W = 0;
    let H = 0;

    // A small deterministic PRNG so the field looks scattered without needing
    // Math.random in the render path (and stays stable across resizes).
    let seed = 0x9e3779b9;
    const rand = () => {
      seed ^= seed << 13;
      seed ^= seed >>> 17;
      seed ^= seed << 5;
      return ((seed >>> 0) % 100000) / 100000;
    };

    const build = () => {
      const rect = parent.getBoundingClientRect();
      W = Math.max(1, Math.round(rect.width));
      H = Math.max(1, Math.round(rect.height));
      canvas.width = W * DPR;
      canvas.height = H * DPR;
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);

      const count = Math.max(10, Math.min(26, Math.round((W * H) / 5200)));
      seed = 0x9e3779b9;
      dots = Array.from({ length: count }, () => {
        const speed = 0.08 + rand() * 0.14; // px/frame, very slow drift
        const dir = rand() * Math.PI * 2;
        return {
          x: rand() * W,
          y: rand() * H,
          vx: Math.cos(dir) * speed,
          vy: Math.sin(dir) * speed,
          r: 1 + rand() * 1.6,
          a: 0.16 + rand() * 0.26,
        };
      });
    };

    const paint = () => {
      ctx.clearRect(0, 0, W, H);
      for (const d of dots) {
        ctx.globalAlpha = d.a;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = colour;
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    const step = () => {
      for (const d of dots) {
        d.x += d.vx;
        d.y += d.vy;
        const m = d.r + 2;
        if (d.x < -m) d.x = W + m;
        else if (d.x > W + m) d.x = -m;
        if (d.y < -m) d.y = H + m;
        else if (d.y > H + m) d.y = -m;
      }
      paint();
    };

    let raf = 0;
    let running = false;
    const loop = () => {
      step();
      raf = requestAnimationFrame(loop);
    };
    const start = () => {
      if (running || reduced) return;
      running = true;
      raf = requestAnimationFrame(loop);
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(raf);
    };

    build();
    paint(); // first frame (also the only frame under reduced motion)

    // Only animate while the card is actually on screen.
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) start();
          else stop();
        }
      },
      { rootMargin: "120px" },
    );
    io.observe(parent);

    // Keep the field matched to the plate as it reflows.
    let resizeRaf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        build();
        paint();
      });
    });
    ro.observe(parent);

    return () => {
      stop();
      cancelAnimationFrame(resizeRaf);
      io.disconnect();
      ro.disconnect();
    };
  }, [tone]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={cn("pointer-events-none absolute inset-0 h-full w-full", className)}
    />
  );
}
